/**
 * NewsSignupMarketoForm
 *
 * A styled wrapper component for embedding a Marketo form specifically
 * for newsletter or signup scenarios using configuration from Sitecore fields.
 *
 * Responsibilities:
 * - Dynamically apply background color and styling based on theme and Sitecore fields
 * - Use the `useMarketoForm` hook to load and render the Marketo form
 * - Generate proper wrapper classes for layout and alignment
 *
 * @param {object} props
 * @param {object} props.fields - Sitecore field values injected via JSS or GraphQL
 */

import React from 'react';
import { useTheme } from '../SharedUtils/hooks'; // Custom hook to detect current theme
import { getBackgroundColor } from '../SharedUtils/themeColors'; // Utility to determine theme-based colors
import { addStyle } from '../SharedUtils/utils'; // Utility to apply inline style keys safely
import useMarketoForm from '../SharedUtils/hooks/useMarketoForm'; // Hook to load/render Marketo forms
import './news-signup-marketo-form.css'; // Component-specific styles

// Custom GraphQL hook to get field values from Sitecore
import { useGetSitecoreFields } from '../../../services/GraphQLService/useGetSitecoreFields';

const NewsSignupMarketoForm = ({ fields }) => {
  // Extract key Sitecore field values
  const formId = fields.MarketoFormId?.value;
  let baseUrl = '';
  let munchkinId = '';
  const tyPage = fields.ThankYouPage?.value;

  // Get the current theme and calculate background color based on content and theme
  const { isLight } = useTheme();
  const backgroundColor = getBackgroundColor(fields, isLight);

  // Build style object with dynamic background color
  const widgetStyles = {};
  addStyle(widgetStyles, 'backgroundColor', backgroundColor);

  // Build a dynamic class list based on field toggles
  const widgetClasses = ['o-widget', 'c-advance-marketo-form'];
  if (fields?.TextAlignCentered?.value) {
    widgetClasses.push('c-advance-marketo-form-centre-align');
  }
  if (fields?.AddPadding?.value) {
    widgetClasses.push('has-padding');
  }

  // Fetch Marketo Form Settings from a Sitecore content item
  const { values } = useGetSitecoreFields(
    '/sitecore/content/Developer2-0/Settings/MarketoForm/MarketoFormSettings',
    ['BaseUrl', 'MunchkinId', 'FreeEmailDomains'],
    'en',
    fields?.EnableEmailValidation // only fetch if validation is enabled
  );

  if (values.BaseUrl && values.MunchkinId) {
    baseUrl = values.BaseUrl;
    munchkinId = values.MunchkinId;
  }

  // Initialize Marketo form using custom hook (but don't clean styles for this layout)
  const { elementId } = useMarketoForm({
    formId,
    baseUrl,
    munchkinId,
    tyPage,
    cleanStyles: false, // allow default Marketo layout/styling
  });

  // Render the form container with applied styles and class names
  return (
    <section className={widgetClasses.join(' ')} style={widgetStyles}>
      <div id={elementId}>
        <form id={`mktoForm_${formId}`}></form>
      </div>
    </section>
  );
};

export default NewsSignupMarketoForm;
